/**
 * Chat Attachment Upload Limits
 *
 * Builds aggregate attachment candidates for chat draft validation.
 *
 * Primary responsibilities:
 * - Count pending file and image attachments together
 * - Preserve plan-based size and batch validation
 *
 * Dependencies:
 * - Chat attachment types
 * - Losi subscription limits
 */

import type { Tables } from '@/supabase/types'
import type { ChatFile } from '@/types/chat-file'
import type { MessageImage } from '@/types/images/message-image'
import { validateAttachmentUpload } from '@/lib/subscription'

type AttachmentCandidate = {
  size: number
}

type DraftAttachmentInput = {
  newMessageFiles: readonly ChatFile[]
  newMessageImages: readonly MessageImage[]
  extraAttachments?: readonly AttachmentCandidate[]
}

const getFileSize = (file: File | null | undefined) =>
  file && Number.isFinite(file.size) ? file.size : 0

export function getChatDraftAttachmentCandidates({
  newMessageFiles,
  newMessageImages,
  extraAttachments = []
}: DraftAttachmentInput): AttachmentCandidate[] {
  return [
    ...newMessageFiles.map(file => ({ size: getFileSize(file.file) })),
    ...newMessageImages.map(image => ({ size: getFileSize(image.file) })),
    ...extraAttachments.map(file => ({ size: file.size }))
  ]
}

export function validateChatDraftAttachmentUpload(
  profile: Tables<'profiles'> | null,
  input: DraftAttachmentInput
) {
  return validateAttachmentUpload(
    profile,
    getChatDraftAttachmentCandidates(input)
  )
}
